import React, { useEffect, useMemo, useRef, useState } from "react";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import { BaseSelect } from "Components/Base/BaseSelect";
import { checkStatusCodeSuccess, notFound } from "Components/constants/Common";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { useFormik } from "formik";
import { submitLabel } from "Components/constants/Role";
import { RepairOrderClosingLabel } from "Components/constants/RepairOrderClosing";
import { getRONumberList } from "Api/RepairOrderOpen";
import { AgGridReact } from "ag-grid-react";
import { makeROC, viewROC } from "Api/RepairOrderClose";
import { toast } from "react-toastify";
import * as yup from "yup";
import Loader from "Components/Base/BaseLoader";
import { Measurements } from "Components/constants/RepairOrderClosing";
import {
  AGGREGATE_SR_NO_LABEL,
  ALL_FIELDS_ARE_REQUIRED,
  AMOUNT_LABEL,
  ARM_LABEL,
  AT_LEAST_ONE_PART_GREATER_ZERO,
  AUTHORIZED_BY_LABEL,
  CLOSING_DATE_LABEL,
  CUST_ID_LABEL,
  CUST_NAME_LABEL,
  DATE_LABEL,
  MEASUREMENTS_VALUE,
  PLEASE_ENTER_PROD_DETAILS,
  QTY_LABEL,
  RATE_LABEL,
  RO_NO_LABEL,
  SERVICE_NAME_LABEL,
  SR_NO_LABEL,
  TOTAL_WORKING_DAYS_LABEL,
  UOM_LABEL,
  VERIFIED_BY_LABEL,
} from "Components/constants/ROModule";
import { REMARKS_LABEL } from "Components/constants/Order";

const RepairOrdClosing = () => {
  const [ROC, setROC] = useState<any[]>([]);
  const [selectedRONo, setSelectedRONo] = useState<string | null>(null);
  const [btnLoader, setBtnLoader] = useState(false);
  const [RONumberList, setRONumberList] = useState<any[]>([]);
  const gridRef = useRef<AgGridReact<any>>(null);
  const [userRole, setUserRole] = useState<string>("");
  const [userId, setUserId] = useState<number | null>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string | null>(null);
  const measurementOptions = Object.values(Measurements);
  const validationSchema = yup.object({
    SelectRONo: yup.string().required(validationMessages.required(RO_NO_LABEL)),
    closing_date: yup
      .date()
      .required(validationMessages.required(CLOSING_DATE_LABEL)),
    authorize_by: yup
      .string()
      .required(validationMessages.required(AUTHORIZED_BY_LABEL)),
  });

  useEffect(() => {
    const sessionData = sessionStorage.getItem("userProfile");
    if (sessionData) {
      const parsedData = JSON.parse(sessionData);
      setUserRole(parsedData.name || "");
      setUserId(parsedData.id || null);
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      SelectRONo: "",
      closing_date: new Date().toISOString().split("T")[0],
      verify_by: userId || "",
      authorize_by: "",
      total_working_days: "",
    },
    validationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const handleSubmit = (values: any) => {
    if (!ROC || ROC.length === 0) {
      toast.error(PLEASE_ENTER_PROD_DETAILS);
      return;
    }

    const missingRequiredFields = ROC.some(
      (row: any) =>
        !row.service_name ||
        !row.aggregate_repair_measurements ||
        !row.measurements_value ||
        row.measurements_value <= 0
    );

    if (missingRequiredFields) {
      toast.error(ALL_FIELDS_ARE_REQUIRED);
      return;
    }
    const validQtyRow = ROC.some((row: any) => row.quantity > 0);
    if (!validQtyRow) {
      toast.error(AT_LEAST_ONE_PART_GREATER_ZERO);
      return;
    }
    const products = ROC.map((item: any) => ({
      id: item.id,
      service_name: item.service_name,
      aggregate_repair_measurements: item.aggregate_repair_measurements,
      measurements_value: item.measurements_value,
      aggregate_sr_no: item.aggregate_sr_no,
    }));

    const payload = {
      repair_order_id: selectedRONo,
      verify_by: values.verify_by || userId || "",
      authorize_by: values.authorize_by,
      closing_date: values.closing_date,
      total_working_days: Number(values.total_working_days),
      product: products,
    };

    setBtnLoader(true);
    makeROC(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          toast.success(res.message);
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setBtnLoader(false);
      });
  };

  useEffect(() => {
    getRONumberList()
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data;
          setRONumberList(
            data.map((item: any) => ({
              label: item.ro_no,
              value: item.id,
            }))
          );
        } else {
          console.error(res.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      });
  }, []);

  useEffect(() => {
    if (selectedRONo) {
      setLoader(true);
      setROC([]);
      viewROC(Number(selectedRONo))
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            const data = res?.data?.[0] || {};

            const partRequisitions = data?.partRequisition || [];

            if (partRequisitions.length > 0) {
              const flatData = partRequisitions.flatMap((item: any) =>
                item.partRequisitionItem.map((part: any, index: number) => {
                  const quantity = part.issue_quantity || 0;
                  const rate = part.productVariant?.rate || 0;
                  const amount = quantity * rate;

                  return {
                    srNo: index + 1,
                    id: part.id,
                    repair_part_order: data.repair_part_order || "",
                    customer_name: data?.customer?.name || "",
                    start_date: data.start_date || "",
                    service_name: part.service_name || "",
                    user_draft_id: data.user_draft_id || "",
                    uom: part.uom || "",
                    quantity: quantity,
                    rate: rate,
                    amount: amount,
                    remarks: part.remarks || "",
                    aggregate_repair_measurements:
                      part.aggregate_repair_measurements || "",
                    aggregate_uom: part.aggregate_uom || "",
                    measurements_value: part.measurements_value || "",
                    aggregate_sr_no: part.aggregate_sr_no || null,
                  };
                })
              );
              setROC(flatData);
            } else {
              setROC([]);
            }

            const fetchedStartDate = data?.start_date;
            setStartDate(fetchedStartDate || null);
          } else {
            toast.error(res.message);
          }
        })
        .catch((err) => {
          //Commented to not throw the error as of now
          // toast.error(err?.response?.data?.message || err?.message);
        })
        .finally(() => {
          setLoader(false);
        });
    } else {
      setROC([]);
    }
  }, [selectedRONo]);

  const calculateWorkingDays = (
    startDate: string,
    closingDate: string
  ): number => {
    const start = new Date(startDate);
    const end = new Date(closingDate);
    let totalWorkingDays = 0;

    const timeDifference = end.getTime() - start.getTime();
    const dayInMilliseconds = 1000 * 3600 * 24;

    if (timeDifference > 0) {
      totalWorkingDays = Math.floor(timeDifference / dayInMilliseconds);
    }

    return totalWorkingDays;
  };

  const ROCloseCols = useMemo(
    () => [
      {
        headerName: SR_NO_LABEL,
        headerTooltip: SR_NO_LABEL,
        valueGetter: "node.rowIndex + 1",
        field: "srNo",
        tooltipField: "srNo",
        flex: 1.5,
        minWidth: 150,
        editable: false,
        sortable: true,
      },
      {
        headerName: SERVICE_NAME_LABEL,
        headerTooltip: SERVICE_NAME_LABEL,
        headerClass: "required",
        field: "service_name",
        tooltipField: "service_name",
        editable: true,
        flex: 2,
        minWidth: 200,
        sortable: true,
      },
      {
        headerName: CUST_ID_LABEL,
        headerTooltip: CUST_ID_LABEL,
        field: "user_draft_id",
        tooltipField: "vendor_id",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: CUST_NAME_LABEL,
        headerTooltip: CUST_NAME_LABEL,
        field: "customer_name",
        tooltipField: "vendor_name",
        editable: false,
        flex: 2,
        minWidth: 200,
        sortable: true,
      },
      {
        headerName: DATE_LABEL,
        headerTooltip: DATE_LABEL,
        field: "start_date",
        tooltipField: "date",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: UOM_LABEL,
        headerTooltip: UOM_LABEL,
        field: "uom",
        tooltipField: "uom",
        editable: false,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: QTY_LABEL,
        headerTooltip: QTY_LABEL,
        field: "quantity",
        tooltipField: "quantity",
        editable: true,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        cellEditor: "agTextCellEditor",
        valueParser: (params: any) => {
          const value = parseFloat(params.newValue) || 0;
          const rate = params.data.rate || 0;
          const newAmount = value * rate;
          params.data.amount = newAmount;
          params.api.refreshCells({
            rowNodes: [params.node],
            columns: ["amount"],
          });

          return value;
        },
      },
      {
        headerName: RATE_LABEL,
        headerTooltip: RATE_LABEL,
        field: "rate",
        tooltipField: "rate",
        editable: true,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        cellEditor: "agTextCellEditor",
        valueParser: (params: any) => {
          const value = parseFloat(params.newValue) || 0;
          const quantity = params.data.quantity || 0;
          const newAmount = value * quantity;
          params.data.amount = newAmount;
          params.api.refreshCells({
            rowNodes: [params.node],
            columns: ["amount"],
          });

          return value;
        },
      },
      {
        headerName: AMOUNT_LABEL,
        headerTooltip: AMOUNT_LABEL,
        field: "amount",
        tooltipField: "amount",
        editable: false,
        flex: 2,
        minWidth: 150,
        sortable: true,
        valueFormatter: (params: any) => {
          return params.value?.toFixed(2) || "0.00";
        },
      },
      {
        headerName: REMARKS_LABEL,
        headerTooltip: REMARKS_LABEL,
        field: "remarks",
        tooltipField: "remarks",
        editable: false,
        flex: 2,
        minWidth: 150,
        sortable: true,
      },
      {
        headerName: ARM_LABEL,
        headerTooltip: ARM_LABEL,
        field: "aggregate_repair_measurements",
        tooltipField: "aggregate_repair_measurements",
        headerClass: "required",
        editable: true,
        flex: 2,
        minWidth: 150,
        sortable: true,
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: measurementOptions,
        },
      },
      {
        headerName: MEASUREMENTS_VALUE,
        headerTooltip: MEASUREMENTS_VALUE,
        field: "measurements_value",
        tooltipField: "measurements_value",
        headerClass: "required",
        editable: true,
        flex: 2,
        minWidth: 150,
        sortable: true,
        cellEditor: "agTextCellEditor",
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
      },
      {
        headerName: AGGREGATE_SR_NO_LABEL,
        headerTooltip: AGGREGATE_SR_NO_LABEL,
        field: "aggregate_sr_no",
        tooltipField: "aggregate_sr_no",
        editable: true,
        flex: 1.5,
        minWidth: 150,
        sortable: true,
        valueParser: (params: any) => {
          const value = params.newValue;
          return value ? parseFloat(value) : 0;
        },
      },
    ],
    []
  );

  document.title = RepairOrderClosingLabel;

  return (
    <div className="page-content">
      {loader && <Loader />}
      <div className="flex-grow-1 mx-3">
        <form onSubmit={formik.handleSubmit}>
          <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
            <span className="fs-4 d-flex gap-4">{RepairOrderClosingLabel}</span>
          </div>
          <div className="card py-2 px-4 overflow-x-auto">
            <div className="d-flex flex-wrap justify-content-between mb-1 pe-auto">
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseSelect
                  name="SelectRONo"
                  label={RO_NO_LABEL}
                  options={RONumberList}
                  value={formik.values.SelectRONo}
                  handleChange={(name: any, value: any) => {
                    formik.setFieldValue(name, value);
                    setSelectedRONo(value === "" ? null : value);
                  }}
                  required
                  handleBlur={formik.handleBlur}
                  error={formik.errors.SelectRONo}
                  touched={formik.touched.SelectRONo}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="closing_date"
                  label={CLOSING_DATE_LABEL}
                  placeholder={SelectPlaceHolder("")}
                  type="date"
                  value={formik.values.closing_date}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newClosingDate = e.target.value;
                    formik.handleChange(e);

                    if (startDate) {
                      const totalDays = calculateWorkingDays(
                        startDate,
                        newClosingDate
                      );
                      formik.setFieldValue(
                        "total_working_days",
                        totalDays.toString()
                      );
                    }
                  }}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.closing_date}
                  touched={formik.touched.closing_date}
                  required
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="verify_by"
                  label={VERIFIED_BY_LABEL}
                  value={userRole}
                  disabled={true}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="authorize_by"
                  label={AUTHORIZED_BY_LABEL}
                  placeholder={InputPlaceHolder(AUTHORIZED_BY_LABEL)}
                  value={formik.values.authorize_by}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.authorize_by}
                  touched={formik.touched.authorize_by}
                  required
                />
              </div>
              <div className="col-12 col-sm-6 col-md-2 col-lg-2 mb-2">
                <BaseInput
                  name="TotalWorkingDays."
                  label={TOTAL_WORKING_DAYS_LABEL}
                  placeholder={InputPlaceHolder(TOTAL_WORKING_DAYS_LABEL)}
                  value={formik.values.total_working_days}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.total_working_days}
                  touched={formik.touched.total_working_days}
                  required
                  disabled={true}
                />
              </div>
            </div>

            <hr />
            <div className="table-css">
              <div className="ag-theme-quartz">
                <AgGridReact
                  ref={gridRef}
                  rowData={ROC}
                  columnDefs={ROCloseCols}
                  defaultColDef={{
                    sortable: true,
                    resizable: true,
                    flex: 1,
                    tooltipComponentParams: {
                      color: "blue",
                    },
                  }}
                  domLayout="normal"
                  getRowHeight={() => 35}
                  overlayNoRowsTemplate={notFound.dataNotFound}
                />
              </div>
            </div>

            <div className="d-flex justify-content-end mb-1 mt-4 w-100">
              <div className="col-12 d-flex justify-content-end mb-0">
                <BaseButton
                  type="submit"
                  label={submitLabel}
                  color="success"
                  loader={btnLoader}
                  disable={btnLoader}
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
export default RepairOrdClosing;
